import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';

import { RegisterComponent } from '../../components/component-lookup';
import { StateService } from '../../services/state.service';
import { MetricService, QuarterlyGroupDetail, QuarterlyGroup } from '../../services/metric.service';
import { MetricQuarterlyGroupComponent } from '../../visuals/metric-quarterly-group/metric-quarterly-group.component';
import { PercentileQuarterlyComponent } from '../../visuals/percentile-quarterly/percentile-quarterly.component';
import { RankQuarterlyComponent } from '../../visuals/rank-quarterly/rank-quarterly.component';
import { VisualService } from '../../services/visual.service';

@RegisterComponent('QuarterlyGroup')
@Component({
  selector: 'app-quarterly-group',
  standalone: true,
  imports: [CommonModule, DatePipe, MetricQuarterlyGroupComponent, PercentileQuarterlyComponent, RankQuarterlyComponent],
  templateUrl: './quarterly-group.component.html',
  styleUrl: './quarterly-group.component.scss'
})
export class QuarterlyGroupComponent {
  paywall = input<boolean>();
  paywallMessage = input<string | null>();

  private metricService = inject(MetricService);
  readonly institution = inject(StateService).institution;
  readonly group = inject(StateService).group;
  readonly filteredGroup = inject(StateService).filteredGroup;
  readonly date = inject(StateService).date;

  readonly isPrinting = inject(StateService).isPrinting;

  metrics = signal<QuarterlyGroup[]>([]);

  computedMetrics = computed(() => {
    const iids = this.filteredGroup().filter(i => i.isSelected).map(i => i.iid) ?? [];
    this.metrics().forEach(metric => {
      if (metric.details) {
        const institutions = metric.details
          .filter(i => iids.includes(i.iid))
          .sort((a, b) => (metric.rankSort == 'L' ? a.value - b.value : b.value - a.value));
        if (institutions.length > 0) {
          metric.groupValue = institutions.map(i => i.value).reduce((a, b) => a + b, 0) / institutions.length;
          metric.difference = ((Math.round(((metric.institutionValue ?? 0) + Number.EPSILON) * 100) / 100) - (Math.round((metric.groupValue + Number.EPSILON) * 100) / 100)) * (metric.rankSort == 'L' ? -1 : 1);
          metric.institutionsReporting = institutions.length + 1;
        }
        else {
          metric.groupValue = undefined;
          metric.difference = 0;
          metric.institutionsReporting = 0;
        }

        institutions.push(metric.details.find(i => i.iid == this.institution()?.iid) ?? {} as QuarterlyGroupDetail);
        institutions.sort((a, b) => (metric.rankSort == 'L' ? a.value - b.value : b.value - a.value));
        const rank = institutions.findIndex(i => i.iid == this.institution()?.iid) + 1;
        metric.institutionRank = rank == 0 ? undefined : rank;

        if (institutions.length > 0) {
          const top = institutions[0];
          metric.topInstitutionIid = top.iid;
          metric.topInstitutionLabel = top.label;
          metric.topInstitutionValue = top.value;
        }
        else {
          metric.topInstitutionIid = undefined;
          metric.topInstitutionLabel = undefined;
          metric.topInstitutionValue = undefined;
        }
      }
    });
    return this.metrics();
  });

  groupedMetrics = computed(() => this.groupBy(this.computedMetrics(), m => m.sectionTitle));
  sections = computed(() => Object.keys(this.groupedMetrics()));

  metric?: QuarterlyGroup;
  loading = false;
  isDrawerOpen = false;

  constructor() {
    effect(() => {
      const group = this.group();
      const date = this.date();
      if (group && date) {
        this.loading = true;
        this.metricService.getGroupQuarterlySummary(this.institution().iid, date, group.groupType, group.groupId)?.subscribe(metrics => {
          setTimeout(() => {
            this.metrics.set(metrics);
            if (this.metric) {
              const metric = this.metrics().find(m => m.metricId == this.metric?.metricId);
              if (metric) {
                this.updateChart(metric);
              }
            }
            this.loading = false;
          });
        });
      }
    });
  }

  openDrawer() {
    this.isDrawerOpen = true;
  }

  closeDrawer() {
    this.isDrawerOpen = false;
  }

  onToggleDetail(metric: QuarterlyGroup) {
    if (this.isDrawerOpen) {
      if (this.metric == metric) {
        this.closeDrawer();
      }
      else {
        this.updateChart(metric);
      }
    }
    else {
      this.openDrawer();
      this.updateChart(metric);
    }
  }

  updateChart(metric: QuarterlyGroup) {
    this.metric = metric;
  }

  getDisplayTitle(metric: QuarterlyGroup): string {
    const indent = '&nbsp;'.repeat(metric.level * 5);
    return `${indent}${metric.rowTitle}`;
  }

  getDisplayValue(displayType: string, value: number): string {
    return VisualService.getValueDisplay(VisualService.getValue(value, displayType) ?? 0, displayType);
  }

  getDisplayDifference(displayType: string, value: number): string {
    const v = VisualService.getValueDisplay(VisualService.getValue(value, displayType) ?? 0, displayType);
    return v.charAt(0) == '-' ? v.substring(1) : v;
  }

  groupBy<T>(arr: T[], fn: (item: T) => string) {
    return arr.reduce<Record<string, T[]>>((prev, curr) => {
      const groupKey = fn(curr);
      const group = prev[groupKey] || [];
      group.push(curr);
      return { ...prev, [groupKey]: group };
    }, {});
  }
}
